/**
 * CLE-2811 (2026-09-28) — Ani: "In AI modeling I also need to see my notes."
 *
 * On a contract-tied run's Contract Terms step: her notes on the contract, read-only, with their line
 * breaks — and, folded under "Saved on this contract", the facts already saved on it, so she can read the
 * agent's proposal beside what the contract already says. The same values are the contract term's
 * "In production now" (`ContractOnFileValues`). A schedule the server cut at its cap expands to what was
 * received and then points to the contract, which holds the whole of it.
 */

import { Button } from "primereact/button";
import { useState } from "react";
import { Link } from "react-router-dom";

import { IContractOnFile } from "../../../store/queries/contractModelDrafts";
import {
  FULL_SCHEDULE_ON_CONTRACT,
  IOnFileRow,
  NO_NOTES_ON_CONTRACT,
  NOT_RECORDED,
  OPEN_CONTRACT,
  SAVED_ON_CONTRACT,
  YOUR_NOTES_ON_CONTRACT,
  contractNotes,
  contractOnFileRows,
  schedulePreview,
} from "./contractOnFile";

const contractHref = (c: IContractOnFile) =>
  `/payer-rules/contracts/${c.contractFeeScheduleId}`;

/** "Open contract", in a new tab: the run page stays where she was. */
function OpenContractLink({
  contract,
  className,
}: {
  contract: IContractOnFile;
  className?: string;
}) {
  return (
    <Link
      to={contractHref(contract)}
      target="_blank"
      rel="noreferrer"
      className={className}
      style={{ color: "var(--primary-color)" }}
    >
      <i
        className="pi pi-external-link mr-1"
        style={{ fontSize: "0.75rem" }}
        aria-hidden
      />
      {OPEN_CONTRACT}
    </Link>
  );
}

/**
 * A schedule's first lines, then "Show all" — a row-per-code schedule would otherwise fill the step. One
 * the server cut short (`truncated`) ends, once all of what was received is showing, with where the rest is.
 */
function ScheduleText({
  text,
  truncated = false,
  contract,
}: {
  text: string;
  truncated?: boolean;
  contract: IContractOnFile;
}) {
  const [expanded, setExpanded] = useState(false);
  const { shown, hiddenLines } = schedulePreview(text);
  const allShown = expanded || hiddenLines === 0;
  return (
    <div>
      <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {expanded ? text : shown}
      </div>
      {truncated && allShown ? (
        <div className="mt-1 text-color-secondary" data-testid="schedule-cut">
          {FULL_SCHEDULE_ON_CONTRACT} ·{" "}
          <OpenContractLink
            contract={contract}
            className="font-medium no-underline"
          />
        </div>
      ) : null}
      {hiddenLines > 0 ? (
        <Button
          type="button"
          className="p-button-text p-button-sm p-0 mt-1"
          label={
            expanded ? "Show fewer" : `Show all (${hiddenLines} more lines)`
          }
          onClick={() => setExpanded((v) => !v)}
        />
      ) : null}
    </div>
  );
}

function RowValue({
  row,
  contract,
}: {
  row: IOnFileRow;
  contract: IContractOnFile;
}) {
  if (row.value === null)
    return <span className="text-color-secondary">{NOT_RECORDED}</span>;
  if (row.schedule)
    return (
      <ScheduleText
        text={row.value}
        truncated={row.truncated}
        contract={contract}
      />
    );
  return (
    <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {row.value}
    </span>
  );
}

/** The saved values as label/value rows. */
export function ContractOnFileValues({
  contract,
}: {
  contract: IContractOnFile;
}) {
  return (
    <dl
      className="m-0 text-sm"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(9rem, max-content) minmax(0, 1fr)",
        gap: "0.35rem 1rem",
      }}
    >
      {contractOnFileRows(contract).map((row) => (
        <div key={row.key} style={{ display: "contents" }}>
          <dt className="text-color-secondary">{row.label}</dt>
          <dd className="m-0">
            <RowValue row={row} contract={contract} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function ContractOnFileCard({
  contract,
}: {
  contract: IContractOnFile;
}) {
  const [open, setOpen] = useState(false);
  const notes = contractNotes(contract);
  return (
    <section
      aria-label={YOUR_NOTES_ON_CONTRACT}
      className="surface-card border-1 surface-border border-round p-3 mb-3"
    >
      <div className="flex align-items-center gap-2 flex-wrap mb-2">
        <i className="pi pi-book text-color-secondary" aria-hidden />
        <span className="font-semibold">{YOUR_NOTES_ON_CONTRACT}</span>
        <OpenContractLink
          contract={contract}
          className="ml-auto text-sm font-medium no-underline"
        />
      </div>
      {notes ? (
        <div
          className="text-sm line-height-3"
          style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          data-testid="contract-notes"
        >
          {notes}
        </div>
      ) : (
        <div className="text-sm text-color-secondary">
          {NO_NOTES_ON_CONTRACT}
        </div>
      )}
      <Button
        type="button"
        className="p-button-text p-button-sm p-0 mt-3"
        icon={open ? "pi pi-chevron-down" : "pi pi-chevron-right"}
        label={SAVED_ON_CONTRACT}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      />
      {open ? (
        <div className="mt-2" data-testid="saved-on-contract">
          <ContractOnFileValues contract={contract} />
        </div>
      ) : null}
    </section>
  );
}

export default ContractOnFileCard;
